import { forwardRef, useEffect, useRef, useState } from 'react';
import { Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router';
import { orderBy } from 'lodash';
import { ReactComponent as NotificationsIcon } from '../../assets/icons/notifications_icon_header.svg';
import { useNotifications } from '../../redux/ducks/notifications';
import { fromUtcToDeviceTimeZoneDateOnly } from '../../utils/dateFormatter';
import { isBooleanFalse, isBooleanTrue, isObjectNotEmpty, isStringNotEmpty, isStringEmpty, isListEmpty } from '../../utils/objectUtils';
import { toDisplayText } from '../../utils/renderValue';
import NotificationIcon from '../Notifications/NotificationIcon';
import styles from './DashboardPageHeader.module.css';

/**
 * Checks whether a notification should be routed to the dedicated messages UI.
 *
 * @param {object} notification - Notification record.
 * @returns {boolean} Whether the notification is message-related.
 */
const isMessageNotification = (notification) => {
	const entityType = (notification?.entityType ?? '').toUpperCase();
	if (entityType === 'ENCOUNTER') return true;
	const ctaUrl = notification?.cta?.url ?? '';
	return isStringNotEmpty(ctaUrl) && ctaUrl.includes('/messages');
};


const PERSONAL_MEDICAL_ENTITY_TYPES = new Set([
	'ORDER',
	'ENCOUNTER',
	'SUBSCRIPTION',
	'PRESCRIPTION',
	'MEALPLAN',
	'USER', // the patient themselves (automation enum calls this "Patient")
]);


/**
 * Filters out non-personal/staff-oriented notifications from the patient header.
 *
 * @param {object} notification - Notification record.
 * @returns {boolean} Whether the notification should be hidden from the patient menu.
 */
const isNonPersonalNotification = (notification) => {
	const entityType = (notification?.entityType ?? '').toUpperCase();
	if (isStringEmpty(entityType)) return false;
	return !PERSONAL_MEDICAL_ENTITY_TYPES.has(entityType);
};

/**
 * Dropdown toggle button for the V2 notifications menu.
 *
 * @param {object} props
 * @param {(event: React.MouseEvent) => void} props.onClick - Dropdown toggle handler.
 * @param {boolean} props.hasUnread - Whether to show the unread indicator.
 * @param {React.Ref<HTMLButtonElement>} ref - Forwarded dropdown ref.
 * @returns {JSX.Element} Notifications toggle button.
 */
const BellToggle = forwardRef(({ onClick, hasUnread }, ref) => (
	<button
		ref={ref}
		type="button"
		onClick={(e) => { e.preventDefault(); onClick(e); }}
		className={`${styles.dashboardPageHeaderIconButton} ${styles.dashboardPageHeaderIconButtonPeach}`}
		aria-label="Notifications"
	>
		<NotificationsIcon aria-hidden="true" />
		{isBooleanTrue(hasUnread) && <span className={styles.dashboardPageHeaderNotificationDot} />}
	</button>
));
BellToggle.displayName = 'BellToggle';

/**
 * Renders the dashboard header notifications menu for patient-facing alerts.
 *
 * @returns {JSX.Element} Notifications dropdown or empty tooltip button.
 */
export const NotificationsMenu = () => {
	const [show, setShow] = useState(false);
	const closeTimeoutRef = useRef(null);
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { notifications, markNotificationAsRead } = useNotifications();

	const visibleNotifications = orderBy(
		(notifications ?? []).filter((n) => !isBooleanTrue(n?.toStaff) && n?.type !== 'SILENT' && !isMessageNotification(n) && !isNonPersonalNotification(n)),
		['createdAt'],
		['desc']
	);

	const hasUnread = visibleNotifications.some((n) => isBooleanFalse(n.read));

	const clearCloseTimeout = () => {
		if (closeTimeoutRef.current) {
			clearTimeout(closeTimeoutRef.current);
			closeTimeoutRef.current = null;
		}
	};

	const openMenu = () => {
		clearCloseTimeout();
		setShow(true);
	};

	const closeMenuWithDelay = () => {
		clearCloseTimeout();
		closeTimeoutRef.current = setTimeout(() => {
			setShow(false);
			closeTimeoutRef.current = null;
		}, 175);
	};

	useEffect(() => () => {
		if (closeTimeoutRef.current) {
			clearTimeout(closeTimeoutRef.current);
		}
	}, []);

	const handleClick = (notification) => {
		if (isBooleanFalse(notification.read)) {
			markNotificationAsRead(notification.id);
		}
		if (isObjectNotEmpty(notification?.cta) && isStringNotEmpty(notification?.cta?.url)) {
			navigate({ pathname: notification.cta.url, search: searchParams.toString() });
		}
	};

	if (isListEmpty(visibleNotifications)) {
		return (
			<OverlayTrigger
				placement="bottom"
				overlay={<Tooltip id="v2-notifications-empty-tooltip">You have no notifications right now.</Tooltip>}
			>
				<button
					type="button"
					className={`${styles.dashboardPageHeaderIconButton} ${styles.dashboardPageHeaderIconButtonPeach}`}
					aria-label="Notifications"
				>
					<NotificationsIcon aria-hidden="true" />
				</button>
			</OverlayTrigger>
		);
	}

	return (
		<Dropdown
			align="end"
			show={show}
			onToggle={(next) => setShow(next)}
			onMouseEnter={openMenu}
			onMouseLeave={closeMenuWithDelay}
		>
			<Dropdown.Toggle as={BellToggle} id="v2-notifications-menu-toggle" hasUnread={isBooleanTrue(hasUnread)} />
			<Dropdown.Menu
				className={`${styles.headerMenu} dropdown-menu-end mt-2`}
				align="end"
				onMouseEnter={openMenu}
				onMouseLeave={closeMenuWithDelay}
			>
				<div className={styles.headerMenuHeader}>
					<span className={styles.headerMenuTitle}>Notifications</span>
				</div>
				<div className={styles.headerMenuList}>
					{visibleNotifications.map((notification) => {
						const isUnread = isBooleanFalse(notification.read);
						const bodyText = toDisplayText(notification?.body);
						return (
							<button
								type="button"
								key={notification.id}
								onClick={() => handleClick(notification)}
								className={`${styles.headerMenuItem} ${styles.headerMenuItemButton} ${isBooleanTrue(isUnread) ? styles.headerMenuItemUnread : ''}`}
							>
								<div className={styles.headerMenuNotificationIcon}>
									<NotificationIcon severity={notification?.severity} type={notification?.type} />
								</div>
								<div className={styles.headerMenuItemContent}>
									<div className={styles.headerMenuItemTopRow}>
										<span className={styles.headerMenuItemName}>{notification?.subject}</span>
										<span className={styles.headerMenuItemDate}>{fromUtcToDeviceTimeZoneDateOnly(notification?.createdAt, 'MMM D')}</span>
									</div>
									{isStringNotEmpty(bodyText) ? (
										<p className={styles.headerMenuItemPreview}>{bodyText}</p>
									) : null}
									{isStringNotEmpty(notification?.cta?.label) ? (
										<span className={styles.headerMenuItemCta}>{notification.cta.label}</span>
									) : null}
								</div>
								{isBooleanTrue(isUnread) && <span className={styles.headerMenuItemUnreadDot} />}
							</button>
						);
					})}
				</div>
			</Dropdown.Menu>
		</Dropdown>
	);
};
